import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2, MapPin, Phone, Mail, Globe, FileText, 
  CheckCircle, Loader2, ArrowLeft, Shield, Plus, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: 'veterinarian', label: 'Veterinarian', icon: '🏥' },
  { value: 'groomer', label: 'Groomer', icon: '✂️' },
  { value: 'trainer', label: 'Trainer', icon: '🎓' },
  { value: 'dog_walker', label: 'Dog Walker', icon: '🚶' },
  { value: 'boarding', label: 'Boarding / Pet Hotel', icon: '🏨' },
  { value: 'pet_store', label: 'Pet Store', icon: '🛒' },
  { value: 'adoption_agency', label: 'Adoption Agency', icon: '❤️' },
  { value: 'rescue', label: 'Rescue Organization', icon: '🆘' },
  { value: 'breeder', label: 'Breeder', icon: '🐕' },
  { value: 'pet_sitter', label: 'Pet Sitter', icon: '🏠' },
  { value: 'daycare', label: 'Doggy Daycare', icon: '☀️' },
  { value: 'other', label: 'Other', icon: '📋' },
];

const PRICE_RANGES = [
  { value: '$', label: '$ - Budget Friendly' },
  { value: '$$', label: '$$ - Moderate' },
  { value: '$$$', label: '$$$ - Premium' },
  { value: '$$$$', label: '$$$$ - Luxury' },
];

const RELATIONSHIPS = [
  { value: 'owner', label: "I'm the owner/employee" },
  { value: 'customer', label: "I'm a customer" },
  { value: 'found', label: "I found this business online" },
  { value: 'recommendation', label: "Someone recommended it" },
  { value: 'other', label: "Other" },
];

const SubmitServiceProvider = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [verificationLinks, setVerificationLinks] = useState(['']);
  const [certifications, setCertifications] = useState(['']);
  
  const [formData, setFormData] = useState({
    business_name: '',
    service_type: '',
    description: '',
    contact_name: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    license_number: '',
    registration_info: '',
    years_in_business: '',
    hours_of_operation: '',
    price_range: '',
    relationship: '',
    notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addVerificationLink = () => {
    setVerificationLinks([...verificationLinks, '']);
  };

  const removeVerificationLink = (index) => {
    setVerificationLinks(verificationLinks.filter((_, i) => i !== index));
  };

  const updateVerificationLink = (index, value) => {
    const updated = [...verificationLinks];
    updated[index] = value;
    setVerificationLinks(updated);
  };

  const addCertification = () => {
    setCertifications([...certifications, '']);
  };

  const removeCertification = (index) => {
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  const updateCertification = (index, value) => {
    const updated = [...certifications];
    updated[index] = value;
    setCertifications(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.service_type || !formData.address || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.description || formData.description.length < 10) {
      toast.error('Please provide a description (at least 10 characters)');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null,
        verification_links: verificationLinks.filter(l => l.trim()),
        certifications: certifications.filter(c => c.trim()),
      };

      const response = await fetch(`${API}/api/service-submissions/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Service provider submitted for review!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-4">
              You need to be logged in to submit a service provider.
            </p>
            <Button onClick={() => navigate('/login')}>
              Log In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Submission Received!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for helping grow our community! Your submission will be reviewed by club administrators.
              You'll be notified once it's approved.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => {
                setSubmitted(false);
                setFormData({
                  business_name: '',
                  service_type: '',
                  description: '',
                  contact_name: '',
                  phone: '',
                  email: '',
                  website: '',
                  address: '',
                  city: '',
                  state: '',
                  postal_code: '',
                  country: 'US',
                  license_number: '',
                  registration_info: '',
                  years_in_business: '',
                  hours_of_operation: '',
                  price_range: '',
                  relationship: '',
                  notes: '',
                });
                setVerificationLinks(['']);
                setCertifications(['']);
              }}>
                Submit Another
              </Button>
              <Button onClick={() => navigate('/services')}>
                View Services
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Submit a Service Provider</h1>
          <p className="text-gray-600 mt-2">
            Help grow our community by submitting a vet, groomer, adoption agency, or other pet service.
            Your submission will be reviewed before being added to our directory.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Basic Information
              </CardTitle>
              <CardDescription>Tell us about the business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    placeholder="e.g., Happy Paws Veterinary Clinic"
                    required
                    data-testid="input-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_type">Service Type *</Label>
                  <Select value={formData.service_type} onValueChange={(v) => handleChange('service_type', v)}>
                    <SelectTrigger data-testid="select-service-type">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <span>{type.icon}</span>
                            <span>{type.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the services offered, specialties, what makes them great..."
                  rows={4}
                  required
                  data-testid="input-description"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Person</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    placeholder="e.g., Dr. Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="contact@business.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://www.business.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="123 Main Street"
                  required
                  data-testid="input-address"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="City"
                    required
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State/Province</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="CA"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    placeholder="12345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="US"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verification Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Verification Information
              </CardTitle>
              <CardDescription>
                Help us verify this business. More information = faster approval!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="license_number">License Number</Label>
                  <Input
                    id="license_number"
                    value={formData.license_number}
                    onChange={(e) => handleChange('license_number', e.target.value)}
                    placeholder="Professional license #"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="years_in_business">Years in Business</Label>
                  <Input
                    id="years_in_business"
                    type="number"
                    value={formData.years_in_business}
                    onChange={(e) => handleChange('years_in_business', e.target.value)}
                    placeholder="e.g., 5"
                  />
                </div>
              </div>

              {/* Certifications */}
              <div className="space-y-2">
                <Label>Certifications</Label>
                {certifications.map((cert, index) => (
                  <div key={`cert-${index}-${cert.slice(0,8)}`} className="flex gap-2">
                    <Input
                      value={cert}
                      onChange={(e) => updateCertification(index, e.target.value)}
                      placeholder="e.g., AAHA Accredited, Fear Free Certified"
                    />
                    {certifications.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeCertification(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addCertification}>
                  <Plus className="w-4 h-4 mr-1" /> Add Certification
                </Button>
              </div>

              {/* Verification Links */}
              <div className="space-y-2">
                <Label>Verification Links</Label>
                <p className="text-sm text-gray-500">
                  Add links to verify the business (Google Business, Yelp, official website, etc.)
                </p>
                {verificationLinks.map((link, index) => (
                  <div key={`vlink-${index}-${link.slice(0,12)}`} className="flex gap-2">
                    <Input
                      type="url"
                      value={link}
                      onChange={(e) => updateVerificationLink(index, e.target.value)}
                      placeholder="https://..."
                    />
                    {verificationLinks.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeVerificationLink(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addVerificationLink}>
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hours">Hours of Operation</Label>
                  <Input
                    id="hours"
                    value={formData.hours_of_operation}
                    onChange={(e) => handleChange('hours_of_operation', e.target.value)}
                    placeholder="Mon-Fri 9am-5pm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_range">Price Range</Label>
                  <Select value={formData.price_range} onValueChange={(v) => handleChange('price_range', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICE_RANGES.map(range => (
                        <SelectItem key={range.value} value={range.value}>
                          {range.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="relationship">Your relationship to this business</Label>
                <Select value={formData.relationship} onValueChange={(v) => handleChange('relationship', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIPS.map(rel => (
                      <SelectItem key={rel.value} value={rel.value}>
                        {rel.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any other information that might help with verification..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-service-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit for Review
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitServiceProvider;
